import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FileDropperDirective } from '@workspaces/utils/file-dropper.directive';
import { SUPPORTED_EXTENSIONS } from '@workspaces/utils/supported-extensions';

@Component({
  selector: 'app-file-selector',
  templateUrl: './file-selector.component.html',
  styleUrls: ['./file-selector.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, FileDropperDirective]
})
export class FileSelectorComponent {
  @Input() multiple = false;
  @Input() allowedExtensions: string[] = SUPPORTED_EXTENSIONS;

  @Output() filesChanged = new EventEmitter<File[]>();

  public selectedFiles: File[] = [];

  get displayExtensions(): string {
    const extensions = this.allowedExtensions.filter(ext => ext.startsWith('.'));
    return Array.from(new Set(extensions)).join(', ');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(input.files);
    input.value = ''; // Reset input to allow selecting same file again
  }

  onFileDropped(fileList: FileList): void {
    this.handleFiles(fileList);
  }

  handleFiles(fileList: FileList | null): void {
    if (!fileList) return;

    const newFiles: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file && this.isFileAllowed(file.name)) {
        newFiles.push(file);
      }
    }

    if (this.multiple) {
      const existingNames = new Set(this.selectedFiles.map(f => f.name));
      const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
      this.selectedFiles = [...this.selectedFiles, ...uniqueNewFiles];
    } else {
      if (newFiles.length > 0) {
        this.selectedFiles = [newFiles[0]];
      }
    }

    this.emitFiles();
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.emitFiles();
  }

  private emitFiles(): void {
    this.filesChanged.emit(this.selectedFiles);
  }

  private isFileAllowed(fileName: string): boolean {
    const lowerCaseFileName = fileName.toLowerCase();
    const extensions = this.allowedExtensions.filter(ext => ext.startsWith('.'));

    return extensions.some(ext => lowerCaseFileName.endsWith(ext));
  }
}
